import { type ReactElement, useState } from 'react';
import { useStore } from 'zustand';
import { compositeSlotsOf, useEditAreaDraftStore } from '@/entities/edit-area';
import { formatMm } from '@/shared/lib';
import { DeleteButton } from '@/shared/ui';
import { applyEditAreaCommand } from '../model/apply-edit-area-command';
import { CylinderSurfaceInspector } from './cylinder-surface-inspector';
import { SurfaceInspector } from './surface-inspector';

/** 합성 설정은 원통 표면이 있는 면에만 둘 수 있어 표면과 함께 지워진다(결정 Q7). */
const COMPOSITE_REMOVAL_NOTICE = ' 마스크·음영·안내선 설정도 함께 빠집니다.';

interface SurfacePanelProps {
  /** 맨 위 '상품 추출하기' 자리를 받는 ref. 작업 화면이 캔버스의 추출 버튼을 이 자리에 그린다. */
  readonly extractSlotRef?: (element: HTMLDivElement | null) => void;
  /**
   * 면의 유일한 표면을 지운다([표면 삭제]). 확인 창·삭제는 작업 화면이 한다(배치·합성 설정도 함께 지워진다고 알린다).
   * 없으면 버튼을 두지 않는다.
   */
  readonly onDeleteSurface?: (surfaceId: string) => void;
}

/**
 * 면당 표면은 1개다. 2개 이상인 기존 정의는 1개가 될 때까지 삭제만 할 수 있다.
 * 인스펙터는 상품 실측 종류가 정한다(실측 종류와 표면 타입은 1:1) — 평면 상품은 평면, 원통 상품은 원통 인스펙터다.
 * 실측 종류와 맞지 않는 표면·mesh 는 잠긴 정의라 입력 폼을 두지 않는다(잠금 안내는 툴바가 한다).
 * 표면이 1개면 지우기는 제목 오른쪽 [표면 삭제]로 한다. 2개 이상이면 이 칸의 목록에서 지운다.
 */
export const SurfacePanel = ({
  extractSlotRef,
  onDeleteSurface,
}: SurfacePanelProps): ReactElement => {
  const store = useEditAreaDraftStore();
  const state = useStore(store);
  const faceKey = state.selection.face;
  const face = state.faces[faceKey];
  const surfaces = face?.surfaces ?? [];
  const only = surfaces.length === 1 ? surfaces[0] : undefined;
  const photoSize = state.draft.colors.find(
    (color) => color.colorOptionId === state.selection.colorOptionId,
  )?.faces[faceKey]?.photoSize;
  const locked = state.locked || state.isUnsupported;
  const remove = (surfaceId: string): void => {
    const count =
      face?.placements.filter((placement) => placement.surfaceId === surfaceId).length ?? 0;
    const composite = face?.composite === undefined ? '' : COMPOSITE_REMOVAL_NOTICE;
    // 표면이 빠지면 그 면의 진행 중 합성 업로드도 함께 취소된다(D-E) — 있을 때만 알린다.
    const pending = compositeSlotsOf(state.operations, faceKey).length;
    const uploads = pending > 0 ? ` 진행 중인 업로드 ${pending}개도 취소됩니다.` : '';
    if (
      window.confirm(`이 표면과 연결된 배치 ${count}개를 삭제하시겠습니까?${composite}${uploads}`)
    )
      state.removeSurface(faceKey, surfaceId);
  };
  const single = surfaces.length <= 1;
  // 표면이 생기거나 값이 바뀌어도(칸 자동 적용·추출·사진 위 조작) 인스펙터를 다시 만들지 않는다 —
  // 인스펙터가 칸만 맞춰 Tab 으로 옮겨 간 칸의 포커스를 지킨다.
  const inspectorKey = `${state.generation}:${faceKey}`;
  const onDirtyChange = (dirty: boolean): void => state.setInputDirty('surface', dirty);
  // '고급: 표면 수치 조정' 펼침 — 인스펙터가 다시 만들어져도 펼친 채 둔다.
  const [advancedOpen, setAdvancedOpen] = useState(false);
  return (
    <section
      id="edit-area-surfaces"
      tabIndex={-1}
      className="space-y-3 rounded-lg border p-4"
      aria-label="표면 설정"
    >
      <div className="flex items-center justify-between gap-2">
        <h2 className="font-semibold">상품 사진의 표면 설정</h2>
        {onDeleteSurface === undefined || only === undefined ? null : (
          <DeleteButton
            label="표면 삭제"
            disabled={locked}
            onClick={() => onDeleteSurface(only.id)}
          />
        )}
      </div>
      {extractSlotRef === undefined ? null : <div ref={extractSlotRef} className="empty:hidden" />}
      {surfaces.length > 1 ? (
        <>
          <p role="alert" className="text-sm text-destructive">
            면마다 표면은 1개만 둘 수 있습니다. 남길 표면 하나만 두고 나머지를 삭제해 주세요.
          </p>
          <ul className="space-y-2">
            {surfaces.map((surface, index) => (
              <li key={surface.id} className="flex flex-wrap items-center gap-2 text-sm">
                표면 {index + 1} ({formatMm(surface.realSize.widthMm)} ×{' '}
                {formatMm(surface.realSize.heightMm)} mm)
                <DeleteButton
                  iconOnly
                  label={`표면 ${index + 1} 삭제`}
                  disabled={locked}
                  onClick={() => remove(surface.id)}
                />
              </li>
            ))}
          </ul>
        </>
      ) : null}
      {single &&
      state.measurementKind === 'CYLINDER' &&
      (only === undefined || only.type === 'cylinder') ? (
        <CylinderSurfaceInspector
          key={inspectorKey}
          surface={only}
          measurement={state.measurement}
          photoSize={photoSize}
          disabled={locked}
          onDirtyChange={onDirtyChange}
          advancedOpen={advancedOpen}
          onAdvancedToggle={setAdvancedOpen}
          onApply={(surface) =>
            applyEditAreaCommand(store, () => state.upsertSurface(faceKey, surface))
          }
        />
      ) : null}
      {single &&
      state.measurementKind === 'FLAT' &&
      (only === undefined || only.type === 'flat') ? (
        <SurfaceInspector
          key={inspectorKey}
          surface={only}
          measurement={state.measurement}
          photoSize={photoSize}
          disabled={locked}
          onDirtyChange={onDirtyChange}
          advancedOpen={advancedOpen}
          onAdvancedToggle={setAdvancedOpen}
          onApply={(surface) =>
            applyEditAreaCommand(store, () => state.upsertSurface(faceKey, surface))
          }
        />
      ) : null}
    </section>
  );
};
